import {
  cancelAdminMarketplaceListing,
  fetchAdminCreatorPayouts,
  fetchAdminDigitalItems,
  fetchAdminEconomyOverview,
  fetchAdminMarketplaceListings,
  updateAdminCreatorPayout,
  updateAdminDigitalItem,
  type AdminCreatorPayout,
  type AdminDigitalItem,
  type AdminEconomyOverview,
  type AdminMarketplaceListing,
} from "@/api/admin";
import { AdminCard, MetricCard } from "@/components/admin/AdminShell";
import { formatInr } from "@/components/admin/adminConstants";
import { Button } from "@/components/ui/Button";
import { useCallback, useEffect, useState } from "react";

export function AdminEconomyTab() {
  const [overview, setOverview] = useState<AdminEconomyOverview | null>(null);
  const [listings, setListings] = useState<AdminMarketplaceListing[]>([]);
  const [items, setItems] = useState<AdminDigitalItem[]>([]);
  const [payouts, setPayouts] = useState<AdminCreatorPayout[]>([]);
  const [listingStatus, setListingStatus] = useState("");
  const [listingType, setListingType] = useState("");
  const [payoutStatus, setPayoutStatus] = useState("");
  const [payoutSearch, setPayoutSearch] = useState("");
  const [busyId, setBusyId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const reload = useCallback(async () => {
    try {
      setError(null);
      const [o, l, d, p] = await Promise.all([
        fetchAdminEconomyOverview(),
        fetchAdminMarketplaceListings(
          listingStatus || undefined,
          listingType || undefined,
        ),
        fetchAdminDigitalItems(),
        fetchAdminCreatorPayouts(payoutStatus || undefined, payoutSearch || undefined),
      ]);
      setOverview(o);
      setListings(l);
      setItems(d);
      setPayouts(p);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to load economy data");
    }
  }, [listingStatus, listingType, payoutStatus, payoutSearch]);

  useEffect(() => {
    void reload();
  }, [reload]);

  async function saveItem(item: AdminDigitalItem) {
    setBusyId(item.id);
    try {
      await updateAdminDigitalItem(item.id, {
        name: item.name,
        description: item.description,
        price_inr_paise: item.price_inr_paise,
        purchasable: item.purchasable,
        active: item.active,
        sort_order: item.sort_order,
      });
      await reload();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Save failed");
    } finally {
      setBusyId(null);
    }
  }

  async function savePayout(payout: AdminCreatorPayout) {
    setBusyId(payout.user_id);
    try {
      await updateAdminCreatorPayout(payout.user_id, {
        status: payout.status,
        razorpay_account_id: payout.razorpay_account_id,
      });
      await reload();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Payout update failed");
    } finally {
      setBusyId(null);
    }
  }

  async function cancelListing(listing: AdminMarketplaceListing) {
    if (!window.confirm(`Cancel listing "${listing.title}"?`)) return;
    setBusyId(listing.id);
    try {
      await cancelAdminMarketplaceListing(listing.id);
      await reload();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Cancel failed");
    } finally {
      setBusyId(null);
    }
  }

  const mp = overview?.marketplace;
  const shop = overview?.digital_shop;
  const pay = overview?.creator_payouts;

  return (
    <div className="space-y-6">
      {error ? <p className="text-sm text-red-600">{error}</p> : null}

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <MetricCard
          label="Active listings"
          value={String(mp?.active ?? 0)}
          sub={`${mp?.auction_active ?? 0} auctions · ${mp?.pending_payment ?? 0} awaiting pay`}
        />
        <MetricCard
          label="Marketplace sales"
          value={formatInr(mp?.sales_inr_paise)}
          sub={`${mp?.sold ?? 0} sold`}
        />
        <MetricCard
          label="Digital shop"
          value={formatInr(shop?.purchases_inr_paise)}
          sub={`${shop?.purchases_paid ?? 0} purchases · ${shop?.catalog_purchasable ?? 0} SKUs`}
        />
        <MetricCard
          label="Creator payouts"
          value={String(pay?.active ?? 0)}
          sub={`${pay?.pending ?? 0} pending · ${pay?.suspended ?? 0} suspended`}
        />
      </div>

      {overview?.direct_payments && Object.keys(overview.direct_payments).length > 0 ? (
        <AdminCard title="Direct payments (paid)">
          <div className="flex flex-wrap gap-3 text-sm">
            {Object.entries(overview.direct_payments).map(([kind, stats]) => (
              <div
                key={kind}
                className="rounded-xl border border-black/[0.06] bg-[#fafafa] px-3 py-2"
              >
                <div className="font-medium capitalize">{kind.replace(/_/g, " ")}</div>
                <div className="text-ink-muted">
                  {stats.paid_count ?? 0} orders · {formatInr(stats.paid_inr_paise)}
                </div>
              </div>
            ))}
          </div>
        </AdminCard>
      ) : null}

      <AdminCard title="Marketplace listings">
        <div className="mb-4 flex flex-wrap gap-2">
          <select
            className="rounded-lg border border-black/10 bg-white px-3 py-2 text-sm"
            value={listingStatus}
            onChange={(e) => setListingStatus(e.target.value)}
          >
            <option value="">All statuses</option>
            <option value="active">Active</option>
            <option value="pending_payment">Pending payment</option>
            <option value="sold">Sold</option>
            <option value="expired">Expired</option>
            <option value="canceled">Canceled</option>
          </select>
          <select
            className="rounded-lg border border-black/10 bg-white px-3 py-2 text-sm"
            value={listingType}
            onChange={(e) => setListingType(e.target.value)}
          >
            <option value="">All types</option>
            <option value="buy_now">Buy now</option>
            <option value="auction">Auction</option>
          </select>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full text-left text-sm">
            <thead className="text-xs text-ink-muted">
              <tr>
                <th className="px-2 py-2">Title</th>
                <th className="px-2 py-2">Type</th>
                <th className="px-2 py-2">Status</th>
                <th className="px-2 py-2">Seller</th>
                <th className="px-2 py-2">Price</th>
                <th className="px-2 py-2">Bid / buyer</th>
                <th className="px-2 py-2">Actions</th>
              </tr>
            </thead>
            <tbody>
              {listings.map((l) => (
                <tr key={l.id} className="border-t border-black/[0.04]">
                  <td className="max-w-[12rem] truncate px-2 py-2 font-medium">{l.title}</td>
                  <td className="px-2 py-2 capitalize">{l.listing_type.replace("_", " ")}</td>
                  <td className="px-2 py-2 capitalize">{l.status.replace("_", " ")}</td>
                  <td className="px-2 py-2">@{l.seller_username ?? "—"}</td>
                  <td className="px-2 py-2 tabular-nums">{formatInr(l.price_paise)}</td>
                  <td className="px-2 py-2 text-xs text-ink-muted">
                    {l.listing_type === "auction" ? (
                      <>
                        {l.bid_count} bids
                        {l.current_bid_paise
                          ? ` · ${formatInr(l.current_bid_paise)}`
                          : ""}
                        {l.high_bidder_username ? ` · @${l.high_bidder_username}` : ""}
                      </>
                    ) : l.buyer_username ? (
                      `@${l.buyer_username}`
                    ) : (
                      "—"
                    )}
                  </td>
                  <td className="px-2 py-2">
                    {["active", "pending_payment", "expired"].includes(l.status) ? (
                      <Button
                        variant="outline"
                        className="px-3 py-1.5 text-xs"
                        disabled={busyId === l.id}
                        onClick={() => void cancelListing(l)}
                      >
                        Cancel
                      </Button>
                    ) : (
                      "—"
                    )}
                  </td>
                </tr>
              ))}
              {listings.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-2 py-6 text-center text-ink-muted">
                    No listings match filters.
                  </td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </div>
      </AdminCard>

      <AdminCard title="Digital shop catalog">
        <div className="overflow-x-auto">
          <table className="min-w-full text-left text-sm">
            <thead className="text-xs text-ink-muted">
              <tr>
                <th className="px-2 py-2">SKU</th>
                <th className="px-2 py-2">Name</th>
                <th className="px-2 py-2">Kind</th>
                <th className="px-2 py-2">INR (paise)</th>
                <th className="px-2 py-2">Shop</th>
                <th className="px-2 py-2">Active</th>
                <th className="px-2 py-2">Owners</th>
                <th className="px-2 py-2">Sort</th>
                <th className="px-2 py-2" />
              </tr>
            </thead>
            <tbody>
              {items.map((item) => (
                <tr key={item.id} className="border-t border-black/[0.04]">
                  <td className="px-2 py-2 font-mono text-xs">{item.sku}</td>
                  <td className="px-2 py-2">
                    <input
                      className="w-full min-w-[8rem] rounded border border-black/10 px-2 py-1"
                      value={item.name}
                      onChange={(e) =>
                        setItems((prev) =>
                          prev.map((x) =>
                            x.id === item.id ? { ...x, name: e.target.value } : x,
                          ),
                        )
                      }
                    />
                  </td>
                  <td className="px-2 py-2 text-xs capitalize">{item.kind.replace("_", " ")}</td>
                  <td className="px-2 py-2">
                    <input
                      type="number"
                      className="w-24 rounded border border-black/10 px-2 py-1 tabular-nums"
                      value={item.price_inr_paise ?? ""}
                      onChange={(e) =>
                        setItems((prev) =>
                          prev.map((x) =>
                            x.id === item.id
                              ? {
                                  ...x,
                                  price_inr_paise: e.target.value
                                    ? Number(e.target.value)
                                    : null,
                                }
                              : x,
                          ),
                        )
                      }
                    />
                  </td>
                  <td className="px-2 py-2">
                    <input
                      type="checkbox"
                      checked={item.purchasable}
                      onChange={(e) =>
                        setItems((prev) =>
                          prev.map((x) =>
                            x.id === item.id ? { ...x, purchasable: e.target.checked } : x,
                          ),
                        )
                      }
                    />
                  </td>
                  <td className="px-2 py-2">
                    <input
                      type="checkbox"
                      checked={item.active}
                      onChange={(e) =>
                        setItems((prev) =>
                          prev.map((x) =>
                            x.id === item.id ? { ...x, active: e.target.checked } : x,
                          ),
                        )
                      }
                    />
                  </td>
                  <td className="px-2 py-2 tabular-nums text-ink-muted">
                    {item.owner_count}
                    {item.equipped_count > 0 ? ` (${item.equipped_count} equipped)` : ""}
                  </td>
                  <td className="px-2 py-2">
                    <input
                      type="number"
                      className="w-16 rounded border border-black/10 px-2 py-1 tabular-nums"
                      value={item.sort_order}
                      onChange={(e) =>
                        setItems((prev) =>
                          prev.map((x) =>
                            x.id === item.id
                              ? { ...x, sort_order: Number(e.target.value) }
                              : x,
                          ),
                        )
                      }
                    />
                  </td>
                  <td className="px-2 py-2">
                    <Button
                      className="px-3 py-1.5 text-xs"
                      disabled={busyId === item.id}
                      onClick={() => void saveItem(item)}
                    >
                      Save
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </AdminCard>

      <AdminCard title="Creator payout accounts">
        <div className="mb-4 flex flex-wrap gap-2">
          <input
            className="min-w-[12rem] flex-1 rounded-lg border border-black/10 bg-white px-3 py-2 text-sm"
            placeholder="Search username or Razorpay account…"
            value={payoutSearch}
            onChange={(e) => setPayoutSearch(e.target.value)}
          />
          <select
            className="rounded-lg border border-black/10 bg-white px-3 py-2 text-sm"
            value={payoutStatus}
            onChange={(e) => setPayoutStatus(e.target.value)}
          >
            <option value="">All statuses</option>
            <option value="pending">Pending</option>
            <option value="active">Active</option>
            <option value="suspended">Suspended</option>
          </select>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full text-left text-sm">
            <thead className="text-xs text-ink-muted">
              <tr>
                <th className="px-2 py-2">Creator</th>
                <th className="px-2 py-2">Razorpay account</th>
                <th className="px-2 py-2">Status</th>
                <th className="px-2 py-2">Paid orders</th>
                <th className="px-2 py-2">Revenue</th>
                <th className="px-2 py-2" />
              </tr>
            </thead>
            <tbody>
              {payouts.map((p) => (
                <tr key={p.user_id} className="border-t border-black/[0.04]">
                  <td className="px-2 py-2">
                    <div>@{p.username ?? "—"}</div>
                    {p.verified_creator ? (
                      <div className="text-xs text-ink-muted">Verified creator</div>
                    ) : null}
                  </td>
                  <td className="px-2 py-2">
                    <input
                      className="w-full min-w-[10rem] rounded border border-black/10 px-2 py-1 font-mono text-xs"
                      value={p.razorpay_account_id ?? ""}
                      onChange={(e) =>
                        setPayouts((prev) =>
                          prev.map((x) =>
                            x.user_id === p.user_id
                              ? { ...x, razorpay_account_id: e.target.value || null }
                              : x,
                          ),
                        )
                      }
                    />
                  </td>
                  <td className="px-2 py-2">
                    <select
                      className="rounded border border-black/10 px-2 py-1 text-sm capitalize"
                      value={p.status}
                      onChange={(e) =>
                        setPayouts((prev) =>
                          prev.map((x) =>
                            x.user_id === p.user_id
                              ? {
                                  ...x,
                                  status: e.target.value as AdminCreatorPayout["status"],
                                }
                              : x,
                          ),
                        )
                      }
                    >
                      <option value="pending">Pending</option>
                      <option value="active">Active</option>
                      <option value="suspended">Suspended</option>
                    </select>
                  </td>
                  <td className="px-2 py-2 tabular-nums">{p.paid_orders_count}</td>
                  <td className="px-2 py-2 tabular-nums">{formatInr(p.paid_inr_paise)}</td>
                  <td className="px-2 py-2">
                    <Button
                      className="px-3 py-1.5 text-xs"
                      disabled={busyId === p.user_id}
                      onClick={() => void savePayout(p)}
                    >
                      Save
                    </Button>
                  </td>
                </tr>
              ))}
              {payouts.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-2 py-6 text-center text-ink-muted">
                    No payout accounts match filters.
                  </td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </div>
      </AdminCard>
    </div>
  );
}
